/**
 * Composant de gestion des états loading et error pour les widgets
 *
 * Centralise le rendu des états de chargement et d'erreur pour éviter
 * la duplication de code entre les différents widgets. Utilise le pattern
 * render props pour permettre un rendu personnalisé du contenu principal.
 *
 * @module components/WidgetStateHandler
 */

import React from 'react';
import PropTypes from 'prop-types';

// MUI Components
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

// Common components
import {ConfigurationError} from './ConfigurationError';

// Utilities
import {getErrorInfo} from '../hooks/useWidgetError';

// Styled components
import {
  LoadingContainer,
  WidgetContainer,
  WidgetHeader,
  ContentContainer,
} from './styled/WidgetViewStyles';

/**
 * Configuration du WidgetStateHandler
 * @typedef {Object} WidgetStateHandlerProps
 * @property {boolean} isLoading - État de chargement actif
 * @property {Error|null} error - Erreur à afficher
 * @property {boolean} embedded - Widget dans une iframe
 * @property {string} displayMode - Mode d'affichage ('fixed' ou 'floating')
 * @property {React.RefObject} containerRef - Référence au conteneur
 * @property {Function} onClose - Handler de fermeture
 * @property {string} [widgetType] - Type de widget pour styles spécifiques
 * @property {string} [loadingMessage] - Message de chargement personnalisé
 * @property {React.ReactNode} children - Contenu à afficher en cas de succès
 */

/**
 * Composant gérant les états loading/error des widgets
 *
 * Affiche automatiquement :
 * - Un spinner de chargement pendant le loading
 * - Un écran d'erreur formaté en cas d'erreur
 * - Le contenu children en cas de succès
 *
 * @param {WidgetStateHandlerProps} props - Props du composant
 * @return {JSX.Element} Rendu approprié selon l'état
 *
 * @example
 * <WidgetStateHandler
 *   isLoading={isCompanyLoading || isDataLoading}
 *   error={currentError}
 *   embedded={embedded}
 *   displayMode={displayMode}
 *   containerRef={containerRef}
 *   onClose={handleCloseWidget}
 *   widgetType="reservations"
 *   loadingMessage="Chargement de la réservation..."
 * >
 *   <ReservationContent {...props} />
 * </WidgetStateHandler>
 */
export function WidgetStateHandler({
  isLoading,
  error,
  embedded,
  displayMode,
  containerRef,
  onClose,
  widgetType = 'reservations',
  primaryColor,
  loadingMessage = 'Chargement...',
  children,
}) {
  // État de chargement : afficher le spinner avec annonce pour les lecteurs d'écran
  if (isLoading && !error) {
    return (
      <WidgetContainer
        embedded={embedded}
        ref={containerRef}
        widgetType={widgetType}
      >
        <LoadingContainer
          role="status"
          aria-live="polite"
          aria-label={loadingMessage}
        >
          <CircularProgress
            size={40}
            sx={{mb: 2}}
            color="primary"
            aria-hidden="true"
          />
          <Typography variant="body2" aria-hidden="true">
            {loadingMessage}
          </Typography>
        </LoadingContainer>
      </WidgetContainer>
    );
  }

  // État d'erreur : afficher l'écran d'erreur formaté avec annonce pour les lecteurs d'écran
  if (error) {
    const errorInfo = getErrorInfo(error);
    // Indisponibilité volontaire (module/vente désactivés) : pas une erreur
    // technique. Header brand au lieu du rouge, annonce polie plutôt qu'alerte.
    const isUnavailable = errorInfo?.severity === 'unavailable';

    return (
      <WidgetContainer
        embedded={embedded}
        ref={containerRef}
        widgetType={widgetType}
        role={isUnavailable ? 'status' : 'alert'}
        aria-live={isUnavailable ? 'polite' : 'assertive'}
        aria-atomic="true"
      >
        <WidgetHeader
          variant={isUnavailable ? 'default' : 'error'}
          primaryColor={isUnavailable ? primaryColor : undefined}
        >
          <Typography variant="subtitle1" component="h2" sx={{fontWeight: 'bold'}}>
            {errorInfo.title}
          </Typography>
          {displayMode === 'floating' && (
            <IconButton
              size="small"
              onClick={onClose}
              sx={{color: 'inherit'}}
              aria-label={isUnavailable ? 'Fermer le widget' : 'Fermer le widget d\'erreur'}
            >
              <CloseIcon fontSize="small" aria-hidden="true" />
            </IconButton>
          )}
        </WidgetHeader>
        <ContentContainer>
          <ConfigurationError
            title={errorInfo.title}
            message={errorInfo.message}
            details={errorInfo.details}
            severity={isUnavailable ? 'unavailable' : 'error'}
            onClose={onClose}
            showCloseButton={embedded && displayMode === 'floating'}
          />
        </ContentContainer>
      </WidgetContainer>
    );
  }

  // État succès : afficher le contenu
  return children;
}

WidgetStateHandler.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.instanceOf(Error),
  ]),
  embedded: PropTypes.bool.isRequired,
  displayMode: PropTypes.oneOf(['fixed', 'floating']).isRequired,
  containerRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({current: PropTypes.any}),
  ]).isRequired,
  onClose: PropTypes.func.isRequired,
  widgetType: PropTypes.oneOf(['reservations', 'giftvouchers']),
  // Couleur primaire de l'établissement, pour teinter le header de l'état
  // « indisponible » comme le widget en fonctionnement
  primaryColor: PropTypes.string,
  loadingMessage: PropTypes.string,
  children: PropTypes.node.isRequired,
};

export default WidgetStateHandler;
